/**
 * AppSkin — the Deeliver mobile app's own UI, rebuilt for the Content Control preview.
 *
 * WHY THIS FILE EXISTS
 * The preview used to be drawn in MUI: Roboto type, `background.paper` (so it went
 * dark with the admin theme), 70x95 product tiles with no price and no add button,
 * red "#1" order badges, and captions like "CATEGORY ICONS" / "BRANDS". It showed
 * the right DATA in the right ORDER, but it looked nothing like the app, so it was
 * useless for judging what a layout change would actually look like on a phone.
 *
 * Everything here is measured off `apps/mobile/app/src` at a 375pt screen, which is
 * the width the phone frame lays out at, so 1 unit here == 1 pt there:
 *
 *   theme/colors.ts                          brand red #FF002C, grey #7C7C7C…
 *   theme/fonts.ts                           Nunito Sans 400/600/700/800
 *   navigators/home.navigator.tsx            red header, D monogram, hamburger
 *   components/NextDelivery                  dark-red store pill, 30h, 12px bold
 *   screens/tabs/HomeScreen/styles.ts        40h "Deliver to:" bar
 *   components/SearchBar/styles.ts           46h outlined search, glass on the right
 *   components/CategoryItemsSwiper/styles.ts section header 18/700 + "View All >" 15/600,
 *                                            78px category tiles, 110px brand tiles
 *   components/ProductItem/*                 159x226 card, 136 image, 18/800 price,
 *                                            green add button, #babfc3 hairlines
 *   components/HomePageBanner*               16pt margins, r8, 0.25 / 0.41 / 0.5 ratios
 *   navigators/app.navigation.tsx            white tab bar, 25px icons, 10px labels
 *
 * Deliberately plain <div>s and inline styles rather than MUI: the preview must render
 * identically whether the admin is in light or dark mode, because the app itself has
 * no dark mode. Admin-only affordances (order number, empty-at-store warnings, diff
 * tinting) live in PreviewPanel and are drawn as quiet overlays on top of this.
 *
 * @module HomeLayoutManager/components/AppSkin
 */

import React, { useEffect } from 'react';

// ============================================================================
// Tokens — apps/mobile/app/src/theme
// ============================================================================

export const APP = {
  red: '#FF002C',
  darkRed: '#D6082C',
  green: '#42C684',
  greenDisabled: '#a8dfc2',
  black: '#000000',
  grey: '#7C7C7C',
  whiteGrey: '#C3C3C3',
  white: '#FFFFFF',
  /** ProductItem's card divider — Shopify-grid hairline, not a MUI border. */
  hairline: '#babfc3',
  /** Skeleton tones are brand-tinted pink in the app, not grey. */
  skeletonBase: '#FFE8EC',
  skeletonHighlight: '#FFCCD5',
  /** Editor-only accent. Blue on purpose: red is the app's colour and would read as chrome. */
  editorAccent: '#2F6FED',
  font: "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const;

/** Screen width the phone frame lays out at, so app pt == preview px. */
export const APP_SCREEN_WIDTH = 375;

/** CategoryItemsSwiper: `paddingHorizontal: 16` on every row's content. */
export const APP_GUTTER = 16;

/** ProductItem in a home swiper: floor(375 / 2.35) x SWIPER_PRODUCT_HEIGHT. */
export const PRODUCT_CARD_WIDTH = Math.floor(APP_SCREEN_WIDTH / 2.35);
export const PRODUCT_CARD_HEIGHT = 226;

/** Fixed section heights from getSlideComponent(), + the 16 marginBottom. */
export const SECTION_MARGIN_BOTTOM = 16;

/** `R 25.00` — utils/formatPrice.ts with CURRENCY.PREFIX = "R ". */
export function formatPrice(value?: number | null): string {
  const n = Number(value);
  return `R ${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
}

// ============================================================================
// One-time document assets: the app's typeface + a few CSS-only behaviours
// ============================================================================

const FONT_LINK_ID = 'deeliver-app-skin-font';
const STYLE_ID = 'deeliver-app-skin-css';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Nunito+Sans:opsz,wght@6..12,400;6..12,600;6..12,700;6..12,800&display=swap';

const SKIN_CSS = `
@keyframes deeliverSkinPulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.45 } }
.deeliver-skin-row {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.deeliver-skin-row::-webkit-scrollbar { display: none; }
.deeliver-skin-clamp2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
`;

/**
 * Loads Nunito Sans and the skin's keyframes/scroll rules, once per document.
 *
 * Scoped to this hook rather than index.html so the admin doesn't pay for a
 * webfont on every screen — only Content Control renders app UI. The fallback
 * stack in APP.font covers a blocked or offline fonts.googleapis.com; the
 * layout is unaffected either way because nothing here is sized off the text.
 */
export function useAppSkinAssets(): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement('link');
      link.id = FONT_LINK_ID;
      link.rel = 'stylesheet';
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }

    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = SKIN_CSS;
      document.head.appendChild(style);
    }
  }, []);
}

// ============================================================================
// Icons — traced from apps/mobile/app/src/assets/icons, same path data
// ============================================================================

/** DIcon.js — the Deeliver "D" monogram in the red header. */
export function DeeliverMonogram({ size = 36, color = APP.white }: { size?: number; color?: string }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 32.355 37.615" aria-hidden focusable="false">
      <path
        fill={color}
        transform="translate(211.279 -500.82)"
        d="M-195.8 500.82c-10.983 0-16.072 7.483-15.426 14.875a14.988 14.988 0 001.81 5.894 1.155 1.155 0 00.731.562.977.977 0 00.85-.177l2.585-2.092a.972.972 0 00.236-1.184 9.783 9.783 0 01-.92-3.172c-.339-3.875 2.656-9.607 10.163-9.607 5.933 0 9.665 3.748 10.236 10.284.683 7.806-3.348 16.881-9.569 16.881a9.908 9.908 0 01-5.585-2.163l-.044-.032a24.257 24.257 0 002.022-4.743l.055-.134a.81.81 0 00.041-.125c.167-.658.994-3.285 3.2-10.31a6.951 6.951 0 012.4-3.583.968.968 0 00.278-1.164l-.847-2a1.14 1.14 0 00-1.146-.691c-3.783.428-6.047 3.53-6.835 6.055l-3.12 10a21.908 21.908 0 01-1.168 2.987l-.015.033c-.681-.441-1.3-.863-1.893-1.268-.314-.213-.622-.423-.928-.628a1.044 1.044 0 00-1.041-.092.922.922 0 00-.509.857l.057 2.946a1.21 1.21 0 00.484.889c.175.134 1.479 1.239 2.526 2.128 1.329 1.127 2.585 2.193 2.952 2.478a15.337 15.337 0 009.917 3.906c11.751 0 16.069-13.216 15.288-22.148-.832-9.536-7.265-15.462-16.785-15.462z"
      />
    </svg>
  );
}

/** HamburgerMenu.js — three 2px rules, rounded caps. */
export function HamburgerGlyph({ width = 24, height = 14, color = APP.white }: { width?: number; height?: number; color?: string }): React.ReactElement {
  return (
    <svg width={width} height={height} viewBox="0 0 24.417 14" aria-hidden focusable="false">
      <g fill="none" stroke={color} strokeLinecap="round" strokeWidth={2}>
        <path d="M1 1h22.417" />
        <path d="M1 7h22.417" />
        <path d="M1 13h22.417" />
      </g>
    </svg>
  );
}

/** Search.js — mirrored magnifier, handle bottom-left. */
export function SearchGlyph({ size = 16, color = APP.grey }: { size?: number; color?: string }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" style={{ transform: 'scaleX(-1)' }} aria-hidden focusable="false">
      <path
        fill={color}
        d="M13.118 1.887a6.442 6.442 0 0 0-9.411 8.789L.129 14.254a.44.44 0 1 0 .622.622L4.329 11.3a6.442 6.442 0 0 0 10.676-4.858 6.4 6.4 0 0 0-1.887-4.555Zm-.619 8.488a5.56 5.56 0 1 1 0-7.866 5.568 5.568 0 0 1 0 7.866Z"
      />
    </svg>
  );
}

/** Add.js — green disc, white plus. Sits on a white shadowed disc of the same size. */
export function AddButton({ size = 24 }: { size?: number }): React.ReactElement {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: APP.white,
        boxShadow: '0 3px 6px rgba(0,0,0,0.28)',
        flexShrink: 0,
      }}
    >
      <svg width={size} height={size} viewBox="0 0 30 30" aria-hidden focusable="false">
        <circle cx={15} cy={15} r={14} fill={APP.green} />
        <line x1={15} y1={8} x2={15} y2={22} stroke="#fff" strokeWidth={3} />
        <line x1={8} y1={15} x2={22} y2={15} stroke="#fff" strokeWidth={3} />
      </svg>
    </div>
  );
}

/**
 * Tab bar glyphs — Home2 / Shop1 / DeeRewards1 / Cart1 / ReferEarn1.
 * Rendered at 25px like the app; the source viewBoxes aren't square, so they
 * letterbox exactly as react-native-svg does with a fixed width/height.
 */
const TAB_GLYPHS: Array<{ label: string; viewBox: string; paths: string[]; circles?: Array<{ cx: number; cy: number; r: number }> }> = [
  {
    label: 'Home',
    viewBox: '0 0 35.66 36.11',
    paths: [
      'M31.1 12.87v17.94a3.2 3.2 0 01-1 2.3 3.28 3.28 0 01-2.3 1h-20a3.28 3.28 0 01-2.3-1 3.2 3.2 0 01-1-2.3V12.46a1 1 0 10-2 0v18.35a5.31 5.31 0 005.3 5.3h20a5.31 5.31 0 005.3-5.3V12.87a1 1 0 10-2 0z',
      'M13.38 35.09V21.66a2 2 0 012-2h4.88a2.06 2.06 0 011.42.59 2 2 0 01.59 1.42v13.42a1 1 0 102 0V21.66a4.06 4.06 0 00-4.05-4.05h-4.83a4.06 4.06 0 00-4.05 4.05v13.43a1 1 0 102 0zM1.69 15.08l14.65-12.7-.67-.77.65.79a1.56 1.56 0 011-.36 1.6 1.6 0 011 .35L34 15.1a1 1 0 001.28-1.59L19.59.81a3.61 3.61 0 00-4.59 0L.35 13.53a1 1 0 001.34 1.55z',
      'M26.53 2.57V8.4a1 1 0 002 0V2.57a1 1 0 00-2 0',
    ],
    circles: [{ cx: 15.91, cy: 26.36, r: 1.34 }],
  },
  {
    label: 'Shop',
    viewBox: '0 0 31.05 40.25',
    paths: [
      'M15.54 18.26c-3.82 0-5.59 2.6-5.37 5.17a5.28 5.28 0 00.63 2.05.43.43 0 00.26.2.33.33 0 00.29-.06l.9-.73a.34.34 0 00.09-.41 3.17 3.17 0 01-.34-1.11A3.25 3.25 0 0115.55 20c2.07 0 3.37 1.31 3.57 3.58.23 2.72-1.17 5.87-3.33 5.87a3.48 3.48 0 01-1.95-.75 8.86 8.86 0 00.7-1.65V27c.06-.23.35-1.14 1.12-3.59a2.41 2.41 0 01.83-1.24.35.35 0 00.1-.41l-.29-.69a.42.42 0 00-.4-.25 2.93 2.93 0 00-2.38 2.11l-1.09 3.48a9.22 9.22 0 01-.4 1l-.65-.41a3.62 3.62 0 01-.32-.22.39.39 0 00-.36 0 .32.32 0 00-.18.3v1a.43.43 0 00.17.31c.06 0 .51.43.88.74l1 .86a5.36 5.36 0 003.45 1.36c4.09 0 5.6-4.6 5.33-7.71a5.78 5.78 0 00-2.24-4.31 6.08 6.08 0 00-3.57-1.07z',
      'M27.85 39.13V38H3.21a1 1 0 01-.7-.29 1 1 0 01-.28-.71v-.11l2.71-25.03a1 1 0 011-.87h19.67a1 1 0 01.66.25 1 1 0 01.32.64L28.82 37v.09a1 1 0 01-1 1v2.23A3.21 3.21 0 0031.05 37v-.29l-2.24-25.03a3.21 3.21 0 00-3.2-2.92H5.92a3.23 3.23 0 00-2.15.82 3.16 3.16 0 00-1 2L0 36.69v.35a3.21 3.21 0 003.21 3.21h24.64z',
      'M9.88 14.06V8.39A6.44 6.44 0 0111.57 4a5.41 5.41 0 014-1.78 5.38 5.38 0 014 1.78 6.39 6.39 0 011.69 4.38v5.67a1.12 1.12 0 002.23 0V8.39a8.69 8.69 0 00-2.28-5.9A7.65 7.65 0 0015.53 0a7.64 7.64 0 00-5.59 2.49 8.64 8.64 0 00-2.28 5.9v5.67a1.11 1.11 0 002.22 0z',
    ],
  },
  {
    label: 'DeeRewards',
    viewBox: '0 0 40.94 43.11',
    paths: [
      'M20.12 7.18l-.52.26 1.52 3.09a.61.61 0 00.44.32l2.25.33-1.63 1.58a.63.63 0 00-.17.52l.39 2.24-2-1.06a.57.57 0 00-.54 0l-2 1.06.38-2.24a.6.6 0 00-.17-.52l-1.62-1.58 2.24-.33a.59.59 0 00.44-.32l1.53-3.09-.52-.26-.52.26.52-.26-.52-.26-1.42 2.82-3.11.46a.59.59 0 00-.47.39.61.61 0 00.15.6L17 13.38l-.53 3.1a.57.57 0 00.23.57.56.56 0 00.61 0l2.79-1.46 2.78 1.46a.56.56 0 00.61 0 .57.57 0 00.23-.57l-.53-3.1 2.25-2.19a.59.59 0 00.15-.6.57.57 0 00-.47-.39L22 9.74l-1.36-2.82a.6.6 0 00-.52-.32.58.58 0 00-.52.32z',
      'M23.36 2.25a10.53 10.53 0 112.12 20.85 11 11 0 01-2.09-.21 1 1 0 10-.4 2 12.74 12.74 0 002.49.25 12.57 12.57 0 000-25.14A12.41 12.41 0 0023 .26a1 1 0 10.41 2z',
      'M32.45 12.56h-1a10.53 10.53 0 11-3.08-7.44 10.51 10.51 0 013.08 7.44h2a12.56 12.56 0 10-3.68 8.89 12.5 12.5 0 003.68-8.89zM1 26.87H0v15.07a1 1 0 00.84 1 13.66 13.66 0 002.07.17 7.07 7.07 0 002.19-.29 3.67 3.67 0 001.06-.54A2.48 2.48 0 007 41.23a1 1 0 00.08-.38V29.66H6l1 .22a3.11 3.11 0 00.07-.68 3.46 3.46 0 00-.15-1 3.14 3.14 0 00-1.86-2.05 5.67 5.67 0 00-2.33-.43 12.59 12.59 0 00-1.91.17 1 1 0 00-.85 1H1l.16 1a10 10 0 011.58-.14 4.27 4.27 0 011.41.2 1.73 1.73 0 01.5.29 1.13 1.13 0 01.31.47 1.59 1.59 0 01.08.51 1.22 1.22 0 010 .21H5l.4.08-.39-.09H5l.4.08-.39-.09a.83.83 0 000 .22v11.22H6l-.94-.39a.43.43 0 01-.17.2 1.86 1.86 0 01-.8.31 5.89 5.89 0 01-1.22.11c-.46 0-.9 0-1.21-.07h-.48v.23-.23.23-.23l-.18 1h1V26.87H1l.16 1z',
      'M6.08 30.8h15.38v-1l-.07 1v-.61l-.08.61v-.61l-.08.61.18.06a1.31 1.31 0 01.52.52 3.82 3.82 0 01.37 1.86v.35h.7l-.54-.09v.09H23l-.54-.09h.1-.1.1-.1a.46.46 0 01-.06.1 1.82 1.82 0 01-.78.51 8 8 0 01-2.61.42v1-1h-6.77a1 1 0 000 2H19a10.76 10.76 0 002.83-.4 4.84 4.84 0 001.35-.61 2.92 2.92 0 001.05-1.16 2 2 0 00.19-.79h-1 1v-.44a6.37 6.37 0 00-.38-2.31 3.69 3.69 0 00-.67-1.14 3.08 3.08 0 00-1.19-.85 2.53 2.53 0 00-.68-.17H6.08a1 1 0 100 2z',
      'M23.52 33.75l14-1.87-.14-1v1-.65.65-.65.65h.08a2.81 2.81 0 01.4.11 1.74 1.74 0 01.37.21 1.58 1.58 0 01.5.63 3.18 3.18 0 01.27 1.41 7.67 7.67 0 01-.12 1.24l1 .17-.25-1-17.6 4.58H6a1 1 0 000 2h16a1.07 1.07 0 00.25 0l17.73-4.62a1 1 0 00.74-.81 9.22 9.22 0 00.15-1.58 5.2 5.2 0 00-.52-2.4 3.69 3.69 0 00-.89-1.13A3.74 3.74 0 0038.1 30a3.09 3.09 0 00-.75-.12h-.15l-14 1.88a1 1 0 10.28 2z',
    ],
  },
  {
    label: 'Refer & Earn',
    viewBox: '0 0 38.33 35.4',
    paths: [
      'M28.8 18.16v-2.37a5.71 5.71 0 00-.6-2.56 6.77 6.77 0 00-2.72-2.82 8.14 8.14 0 00-4.09-1.07H1.75a7.77 7.77 0 00-1 .06.88.88 0 00-.78.87v24.2a.88.88 0 00.78.87 7.77 7.77 0 001 .06h19.64a8 8 0 005.19-1.84 6.54 6.54 0 001.62-2.05 5.71 5.71 0 00.6-2.51v-2.42a.88.88 0 00-1.76 0V29a3.92 3.92 0 01-.42 1.78 5.13 5.13 0 01-2 2.08 6.5 6.5 0 01-3.22.83H1.75a6.64 6.64 0 01-.77 0l-.1.88h.88v-24.3H.88l.1.87a6.62 6.62 0 01.77 0h19.64a6.22 6.22 0 014.05 1.42A4.63 4.63 0 0126.62 14a3.89 3.89 0 01.38 1.79v2.37a.88.88 0 001.76 0z',
      'M7.84 11.06l14.54-4.19 1 4.13a.88.88 0 101.71-.43l-1.26-5a.89.89 0 00-.38-.57.85.85 0 00-.68-.08L7.35 9.37a.88.88 0 00.49 1.69z',
      'M5.42 11l11.29-7.27 1.54 3a.88.88 0 001.57-.8l-2-3.88a.89.89 0 00-.55-.45.93.93 0 00-.71.11L4.47 9.48a.87.87 0 00-.27 1.21.89.89 0 001.22.31zM27 18.16v-.88h-3a3.15 3.15 0 00-2.28 1 3.39 3.39 0 00-.93 2.34v3.52a3.35 3.35 0 00.93 2.33 3.12 3.12 0 002.28 1h6.87a.89.89 0 00.88-.88v-8.43a.9.9 0 00-.26-.62.85.85 0 00-.62-.26H27V19h3v6.7h-6a1.39 1.39 0 01-1-.44 1.64 1.64 0 01-.44-1.13v-3.52a1.62 1.62 0 01.44-1.13 1.39 1.39 0 011-.48h3z',
      'M28.88 5.65a4.38 4.38 0 012.42 1.51 4.32 4.32 0 01.95 2.71v.34a.88.88 0 00.81.94.87.87 0 00.94-.81 3.7 3.7 0 000-.47 6.07 6.07 0 00-4.75-5.93.88.88 0 00-1.05.66.87.87 0 00.68 1.05z',
      'M26 2.29a7.63 7.63 0 012.5-.52h.33a7.76 7.76 0 017.74 7.41v.34a7.8 7.8 0 01-.31 2.17.88.88 0 001.69.5 9.47 9.47 0 00.38-2.67v-.41A9.5 9.5 0 0028.83 0h-.41a9.38 9.38 0 00-3.06.64.88.88 0 10.64 1.65zM11.43 28.24a9.15 9.15 0 01-3.52-.81l.68-2.28a5.94 5.94 0 001.43.53 9 9 0 001.41.23V23.6l-.25-.07a5.18 5.18 0 01-2.46-1.34 3 3 0 01-.86-2.12 2.87 2.87 0 011-2.22 4.33 4.33 0 012.62-1v-1.57h1.4v1.57a8.89 8.89 0 013.06.8L15 19.87a4.84 4.84 0 00-2.2-.7v2.22l.2.05a4.84 4.84 0 012.4 1.28 2.92 2.92 0 01.85 2.1 3.07 3.07 0 01-.9 2.31 4.29 4.29 0 01-2.55 1.08v1.24h-1.4zm0-9.07c-.57.13-.86.42-.86.88a.77.77 0 00.18.52 1.77 1.77 0 00.68.4zM13.61 25a.89.89 0 00-.16-.56 1.38 1.38 0 00-.62-.4v1.75c.52-.11.78-.37.78-.79z',
    ],
    circles: [{ cx: 25.43, cy: 22.47, r: 0.85 }],
  },
  {
    label: 'Cart',
    viewBox: '0 0 40.41 34.29',
    paths: [
      'M1 2h6l6.4 24.83a1 1 0 001 .77h20.2a1 1 0 100-2H15.16L8.76.77a1 1 0 00-1-.77H1a1 1 0 000 2z',
      'M21.44 29.94h-1a2.29 2.29 0 11-.67-1.63 2.29 2.29 0 01.67 1.63h2A4.34 4.34 0 1021.19 33a4.31 4.31 0 001.27-3.08zM34.57 29.94h-1a2.29 2.29 0 11-.67-1.63 2.29 2.29 0 01.67 1.63h2A4.34 4.34 0 1034.32 33a4.36 4.36 0 001.27-3.08zM9.23 7.7h28.84l-3.45 13.5H13.37a1 1 0 100 2h22a1 1 0 001-.77l4-15.54a1 1 0 00-1-1.27H9.23a1 1 0 100 2z',
      'M11.23 15H37.4a.58.58 0 000-1.16H11.23a.58.58 0 000 1.16',
      'M18.81 6.72l1.26 15.55a.58.58 0 101.16-.1L20 6.63a.57.57 0 00-.62-.53.58.58 0 00-.54.62M28.76 6.62l-1.42 15.55a.57.57 0 00.52.63.59.59 0 00.64-.53l1.42-15.54a.58.58 0 00-.53-.63.57.57 0 00-.63.52',
    ],
  },
];

// ============================================================================
// Fixed chrome — everything that does NOT scroll on the home screen
// ============================================================================

/**
 * iOS status bar over the red header. The app's header is `insets.top + 56` tall
 * with `paddingTop: insets.top + 10`, so the red runs all the way up behind it.
 */
export function AppStatusBar(): React.ReactElement {
  return (
    <div
      style={{
        height: 44,
        background: APP.red,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        padding: '0 22px 2px',
        color: APP.white,
        fontFamily: APP.font,
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      <span style={{ letterSpacing: '-0.02em' }}>9:41</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        {/* signal */}
        <svg width={16} height={11} viewBox="0 0 16 11" aria-hidden focusable="false">
          {[0, 1, 2, 3].map((i) => (
            <rect key={i} x={i * 4} y={8 - i * 2.5} width={2.6} height={3 + i * 2.5} rx={0.8} fill="#fff" />
          ))}
        </svg>
        {/* wifi */}
        <svg width={15} height={11} viewBox="0 0 15 11" aria-hidden focusable="false">
          <path d="M7.5 9.6 5.8 7.8a2.4 2.4 0 0 1 3.4 0Z" fill="#fff" />
          <path d="M3.9 5.9a5.1 5.1 0 0 1 7.2 0" stroke="#fff" strokeWidth={1.4} fill="none" strokeLinecap="round" />
          <path d="M1.6 3.6a8.4 8.4 0 0 1 11.8 0" stroke="#fff" strokeWidth={1.4} fill="none" strokeLinecap="round" />
        </svg>
        {/* battery */}
        <svg width={25} height={12} viewBox="0 0 25 12" aria-hidden focusable="false">
          <rect x={0.5} y={0.5} width={21} height={11} rx={3} stroke="#fff" strokeOpacity={0.5} fill="none" />
          <rect x={2} y={2} width={18} height={8} rx={1.8} fill="#fff" />
          <path d="M23 4.2v3.6a2 2 0 0 0 0-3.6Z" fill="#fff" fillOpacity={0.5} />
        </svg>
      </span>
    </div>
  );
}

/**
 * Red header from home.navigator.tsx: D monogram left, the NextDelivery store
 * pill centred, hamburger right. The pill is a Liquid Glass view on iOS and a
 * solid dark-red one everywhere else — solid is the honest thing to draw here.
 */
export function AppHeaderBar({ storeLabel }: { storeLabel?: string | null }): React.ReactElement {
  return (
    <div
      style={{
        height: 56,
        background: APP.red,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        flexShrink: 0,
        position: 'relative',
        zIndex: 2,
      }}
    >
      <div style={{ width: 44, display: 'flex', alignItems: 'center' }}>
        <DeeliverMonogram size={36} />
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {storeLabel ? (
          <div
            style={{
              height: 30,
              minWidth: 140,
              borderRadius: 50,
              background: APP.darkRed,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 16px',
              color: APP.white,
              fontFamily: APP.font,
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {storeLabel}
          </div>
        ) : (
          // No store selected: the app renders an empty bar, not a placeholder pill.
          <div style={{ height: 30 }} />
        )}
      </div>
      <div style={{ width: 44, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <HamburgerGlyph />
      </div>
    </div>
  );
}

/** HomeScreen's `DeliverTo` — 40h, shadowed, address centred and flexed. */
export function AppDeliverToBar({ address }: { address?: string | null }): React.ReactElement {
  return (
    <div
      style={{
        height: 40,
        background: APP.white,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 16px',
        boxShadow: '0 1px 2.4px rgba(0,0,0,0.45)',
        fontFamily: APP.font,
        fontSize: 14,
        flexShrink: 0,
        position: 'relative',
        zIndex: 1,
      }}
    >
      <span style={{ color: APP.black, fontWeight: 700 }}>Deliver to:</span>
      {address ? (
        <>
          <span style={{ flex: 1, textAlign: 'center', color: APP.grey, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {address}
          </span>
          <span style={{ color: APP.red }}>Change</span>
        </>
      ) : (
        <>
          <span style={{ flex: 1, textAlign: 'center', color: APP.grey, fontStyle: 'italic' }}>No address yet</span>
          <span style={{ color: APP.red }}>Add</span>
        </>
      )}
    </div>
  );
}

/** FakeSearchBar — 46h outlined field, "Search..." left, magnifier right. */
export function AppSearchField(): React.ReactElement {
  return (
    <div style={{ background: APP.white, padding: '8px 16px', flexShrink: 0 }}>
      <div
        style={{
          height: 46,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          border: `1px solid ${APP.whiteGrey}`,
          borderRadius: 8,
          boxSizing: 'border-box',
        }}
      >
        <span style={{ fontFamily: APP.font, fontWeight: 600, fontSize: 14, color: APP.grey }}>Search...</span>
        <SearchGlyph size={15} />
      </div>
    </div>
  );
}

/**
 * Bottom tab bar. The app uses the platform tab bar (opaque white, 1px hairline,
 * black 10px labels, red tint on the active tab) — not a floating pill.
 */
export function AppTabBar({ active = 'Home' }: { active?: string }): React.ReactElement {
  return (
    <div
      style={{
        background: APP.white,
        borderTop: '1px solid #e0e0e0',
        boxShadow: '0 -6px 16px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-around',
        paddingTop: 8,
        height: 56,
        flexShrink: 0,
        fontFamily: APP.font,
      }}
    >
      {TAB_GLYPHS.map(({ label, viewBox, paths, circles }) => {
        const isActive = label === active;
        const color = isActive ? APP.red : APP.black;
        return (
          <div
            key={label}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              minWidth: 0,
              flex: 1,
            }}
          >
            <svg width={25} height={25} viewBox={viewBox} aria-hidden focusable="false">
              {paths.map((d) => (
                <path key={d.slice(0, 24)} d={d} fill={color} />
              ))}
              {circles?.map((c) => (
                <circle key={`${c.cx}-${c.cy}`} cx={c.cx} cy={c.cy} r={c.r} fill={color} />
              ))}
            </svg>
            <span
              style={{
                fontSize: 10,
                color,
                fontWeight: isActive ? 700 : 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Scrolling content — CategoryItemsSwiper and its three slide types
// ============================================================================

/**
 * `CategoryItemsSwiperTitleView` + `CategoryTitle` + `ViewAll`.
 *
 * `order` and `adornment` are the admin affordances kept inside app chrome: the
 * home-screen position, and whatever the section wants to flag (empty at this
 * store, which filter fills it). Both are drawn deliberately quiet so the row
 * still reads as the app's own header rather than an editor row.
 */
export function AppSectionHeader({
  title,
  order,
  onViewAll,
  hideViewAll = false,
  adornment,
}: {
  title: string;
  order?: number;
  onViewAll?: () => void;
  hideViewAll?: boolean;
  adornment?: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: `0 ${APP_GUTTER}px 4px`,
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        {typeof order === 'number' && (
          <span
            title={`Position ${order} on the home screen`}
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              borderRadius: 4,
              background: 'rgba(0,0,0,0.06)',
              color: APP.grey,
              fontFamily: APP.font,
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {order}
          </span>
        )}
        <span
          style={{
            fontFamily: APP.font,
            fontSize: 18,
            fontWeight: 700,
            color: APP.black,
            paddingLeft: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </span>
        {adornment}
      </div>
      {!hideViewAll && (
        <span
          onClick={onViewAll}
          style={{
            fontFamily: APP.font,
            fontSize: 15,
            fontWeight: 600,
            color: APP.red,
            cursor: onViewAll ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {'View All >'}
        </span>
      )}
    </div>
  );
}

/** Horizontal swiper body: 16pt content padding, no visible scrollbar. */
export function AppRow({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}): React.ReactElement {
  return (
    <div className="deeliver-skin-row" style={{ background: APP.white, padding: `0 ${APP_GUTTER}px`, ...style }}>
      {children}
    </div>
  );
}

export interface SkinProduct {
  name: string;
  price?: number | null;
  imageUrl?: string | null;
}

/**
 * ProductItem as the home swiper renders it: 159x226, `wrapperPadding "12px 8px"`,
 * `paddingBottom 14`, image contained at 136, 2-line 14/600 title, 18/800 price,
 * green add button. The right/bottom `#babfc3` hairlines are the card's own —
 * they're what separates adjacent cards, which have no gap between them.
 */
export function AppProductCard({ product, loading = false }: { product?: SkinProduct; loading?: boolean }): React.ReactElement {
  const shell: React.CSSProperties = {
    width: PRODUCT_CARD_WIDTH,
    minWidth: PRODUCT_CARD_WIDTH,
    height: PRODUCT_CARD_HEIGHT,
    boxSizing: 'border-box',
    background: APP.white,
    padding: '12px 8px 14px',
    borderRight: `1px solid ${APP.hairline}`,
    borderBottom: `1px solid ${APP.hairline}`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    fontFamily: APP.font,
    marginTop: 6,
  };

  if (loading || !product) {
    return (
      <div style={shell}>
        <div
          style={{
            width: '100%',
            height: 136,
            background: APP.skeletonBase,
            borderRadius: 4,
            animation: 'deeliverSkinPulse 1.5s ease-in-out infinite',
          }}
        />
        <div style={{ width: '100%' }}>
          <div style={{ width: '90%', height: 12, background: APP.skeletonBase, borderRadius: 3, marginBottom: 6 }} />
          <div style={{ width: '60%', height: 12, background: APP.skeletonBase, borderRadius: 3, marginBottom: 10 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ width: 62, height: 18, background: APP.skeletonHighlight, borderRadius: 3 }} />
            <div style={{ width: 24, height: 24, background: APP.skeletonHighlight, borderRadius: '50%' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={shell}>
      {product.imageUrl ? (
        <img
          src={product.imageUrl}
          alt={product.name}
          style={{ width: '100%', height: 136, objectFit: 'contain', background: APP.white }}
        />
      ) : (
        <div style={{ width: '100%', height: 136, background: '#F4F4F4' }} />
      )}
      <div style={{ width: '100%' }}>
        <div
          className="deeliver-skin-clamp2"
          style={{ fontSize: 14, fontWeight: 600, lineHeight: '18px', minHeight: 36, color: APP.black }}
        >
          {product.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 800, lineHeight: '24px', color: APP.black, fontVariantNumeric: 'tabular-nums' }}>
            {formatPrice(product.price)}
          </span>
          <AddButton />
        </div>
      </div>
    </div>
  );
}

/**
 * CategorySlide: 78x78 red tile, r14, elevated; the category's own SVG at 60x60
 * inside it (the icons are authored white, so they're used as-is, not tinted);
 * one-line 14/700 label under it.
 */
export function AppCategoryTile({
  name,
  imageUrl,
  dimmed = false,
}: {
  name: string;
  imageUrl?: string | null;
  dimmed?: boolean;
}): React.ReactElement {
  return (
    <div
      style={{
        width: 78,
        marginRight: 22,
        marginTop: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        fontFamily: APP.font,
        opacity: dimmed ? 0.45 : 1,
      }}
    >
      <div
        style={{
          width: 78,
          height: 78,
          borderRadius: 14,
          background: APP.red,
          boxShadow: '0 6px 8px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={name} style={{ width: 60, height: 60, objectFit: 'contain' }} />
        ) : (
          <span style={{ color: APP.white, fontSize: 11, fontWeight: 700, textAlign: 'center', padding: 4 }}>
            {name.slice(0, 3).toUpperCase()}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          lineHeight: '18px',
          paddingTop: 8,
          textAlign: 'center',
          width: 78,
          color: APP.black,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {name}
      </div>
    </div>
  );
}

/** BrandSlide: 110x110 white card, r8, elevated, logo contained at 90. No label. */
export function AppBrandTile({ imageUrl, name }: { imageUrl?: string | null; name: string }): React.ReactElement {
  return (
    <div
      style={{
        width: 110,
        height: 110,
        minWidth: 110,
        marginRight: 12,
        marginTop: 8,
        marginBottom: 8,
        borderRadius: 8,
        background: APP.white,
        boxShadow: '0 6px 8px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontFamily: APP.font,
      }}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={name} style={{ maxWidth: 90, width: '100%', height: 90, objectFit: 'contain' }} />
      ) : (
        <span style={{ fontSize: 12, fontWeight: 700, color: APP.grey, textAlign: 'center', padding: 6 }}>{name}</span>
      )}
    </div>
  );
}

/** Pagination dots under the slider banner: 8px, red active, #C3C3C3 inactive. */
export function AppBannerDots({
  count,
  active,
  onSelect,
}: {
  count: number;
  active: number;
  onSelect?: (index: number) => void;
}): React.ReactElement {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 10 }}>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          onClick={() => onSelect?.(i)}
          style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            margin: '0 3px',
            background: i === active ? APP.red : APP.whiteGrey,
            cursor: onSelect ? 'pointer' : 'default',
          }}
        />
      ))}
    </div>
  );
}

/** Banner slot: 16pt side margins, r8, elevated, `width * ratio` tall. */
export function AppBannerFrame({
  ratio,
  children,
  style,
}: {
  ratio: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
}): React.ReactElement {
  return (
    <div
      style={{
        margin: `0 ${APP_GUTTER}px`,
        borderRadius: 8,
        background: APP.whiteGrey,
        boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
        overflow: 'hidden',
        aspectRatio: `1 / ${ratio}`,
        position: 'relative',
        ...style,
      }}
    >
      {children}
    </div>
  );
}
